import React, { isValidElement, useLayoutEffect, useEffect, useRef, useState, useMemo, useCallback } from 'react';

const REACT_ELEMENT_TYPE = Symbol.for('react.element');
const REACT_FRAGMENT_TYPE = Symbol.for('react.fragment');
const isValidObject = (object) => typeof object === 'object' && object !== null;
const isFragment = (object) => {
    if (!isValidObject(object))
        return false;
    const $$typeof = object.$$typeof;
    if ($$typeof !== REACT_ELEMENT_TYPE)
        return false;
    const type = object.type;
    return type === REACT_FRAGMENT_TYPE;
};
const flattenChildren = (children, depth = 0, keys = []) => {
    return React.Children.toArray(children).reduce((acc, node, nodeIndex) => {
        if (isValidElement(node) && isFragment(node)) {
            acc.push.apply(acc, flattenChildren(node.props.children, depth + 1, keys.concat(node.key || nodeIndex)));
        }
        else {
            if (React.isValidElement(node)) {
                acc.push(React.cloneElement(node, {
                    key: keys.concat(String(node.key)).join('.'),
                }));
            }
            else if (typeof node === 'string' || typeof node === 'number') {
                acc.push(node);
            }
        }
        return acc;
    }, []);
};

// const isSSR = () => typeof window === "undefined"
const getUserAgent = () => {
    var _a;
    if (typeof navigator !== 'undefined')
        return navigator.userAgent || navigator.vendor;
    if (typeof window === "undefined")
        return '';
    return (_a = window.opera) !== null && _a !== void 0 ? _a : '';
};
const isTabletOrMobile = (device) => {
    const regexUserAgent = new RegExp('(android|bb\\d+|meego).+mobile|avantgo|bada\\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|' +
        'netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\\.(browser|link)|vodafone|wap|windows ce|xda|xiino', 'i');
    const regexSubstringUserAgent = new RegExp('1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|' +
        'au(di|\\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\\-(n|u)|c55\\/|capi|ccwa|cdm\\-|cell|chtm|cldc|cmd\\-|co(mp|nd)|craw|' +
        'da(it|ll|ng)|dbte|dc\\-s|devi|dica|dmob|do(c|p)o|ds(12|\\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\\-|_)|g1 u|g560|gene|' +
        'gf\\-5|g\\-mo|go(\\.w|od)|gr(ad|un)|haie|hcit|hd\\-(m|p|t)|hei\\-|hi(pt|ta)|hp( i|ip)|hs\\-c|ht(c(\\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\\-(20|go|ma)|i230|' +
        'iac( |\\-|\\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\\/)|klon|kpt |kwc\\-|kyo(c|k)|le(no|xi)|' +
        'lg( g|\\/(k|l|u)|50|54|\\-[a-w])|libw|lynx|m1\\-w|m3ga|m50\\/|ma(te|ui|xo)|mc(01|21|ca)|m\\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|' +
        't(\\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|' +
        'oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\\-2|po(ck|rt|se)|prox|psio|pt\\-g|qa\\-a|qc(07|12|21|32|60|\\-[2-7]|i\\-)|' +
        'qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\\-|oo|p\\-)|sdk\\/|se(c(\\-|0|1)|47|mc|nd|ri)|sgh\\-|shar|sie(\\-|m)|sk\\-0|' +
        'sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\\-|v\\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\\-|tdg\\-|tel(i|m)|tim\\-|t\\-mo|to(pl|sh)|' +
        'ts(70|m\\-|m3|m5)|tx\\-9|up(\\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\\-| )|' +
        'webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\\-|your|zeto|zte\\-', 'i');
    return (regexUserAgent.test(device) ||
        regexSubstringUserAgent.test(device.substr(0, 4)) ||
        /Tablet|iPad/i.test(device));
};
const getTypeOfDevice = ({ breakpoints }) => {
    var _a, _b;
    const mobileBreakpoint = (_a = breakpoints === null || breakpoints === void 0 ? void 0 : breakpoints.mobile) !== null && _a !== void 0 ? _a : 768;
    const tabletBreakpoint = (_b = breakpoints === null || breakpoints === void 0 ? void 0 : breakpoints.tablet) !== null && _b !== void 0 ? _b : 1024;
    const mobileOrTablet = isTabletOrMobile(getUserAgent());
    if (typeof screen === "undefined")
        return 'desktop';
    const isTablet = screen.width >= mobileBreakpoint && screen.width < tabletBreakpoint;
    const isMobile = screen.width < mobileBreakpoint;
    if (mobileOrTablet && isTablet)
        return 'tablet';
    if (mobileOrTablet && isMobile)
        return 'mobile';
    return 'desktop';
};

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function useEventListener(eventName, handler, element, options) {
    // Create a ref that stores handler
    const savedHandler = useRef(handler);
    useIsomorphicLayoutEffect(() => {
        savedHandler.current = handler;
    }, [handler]);
    useEffect(() => {
        var _a;
        // Define the listening target
        const targetElement = (_a = element === null || element === void 0 ? void 0 : element.current) !== null && _a !== void 0 ? _a : window;
        if (!(targetElement && targetElement.addEventListener))
            return;
        // Create event listener that calls handler function stored in ref
        const listener = event => savedHandler.current(event);
        targetElement.addEventListener(eventName, listener, options);
        // Remove event listener on cleanup
        return () => {
            targetElement.removeEventListener(eventName, listener, options);
        };
    }, [eventName, element, options]);
}

const useScrollPosition = () => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const updatePosition = () => {
        setScrollPosition(window.pageYOffset);
    };
    useEventListener("scroll", updatePosition);
    useEffect(() => {
        updatePosition();
    }, []);
    return scrollPosition;
};

const useObserveElements = (options, targetRef) => {
    const [elements, setElements] = useState([]);
    const [elementInView, setElementInView] = useState(undefined);
    const [isVisible, setIsVisible] = useState(false);
    const observer = useRef(null);
    const observerOptions = options || {
        threshold: 0,
        rootMargin: "-50% 0px",
        root: null
    };
    useEffect(() => {
        if (!elements.length)
            return;
        observer.current = new IntersectionObserver(observedEntries => {
            setElementInView(observedEntries.find(entry => entry.isIntersecting));
        }, observerOptions);
        elements.forEach(element => {
            var _a;
            (_a = observer.current) === null || _a === void 0 ? void 0 : _a.observe(element);
        });
        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elements]);
    useEffect(() => {
        if ((targetRef === null || targetRef === void 0 ? void 0 : targetRef.current) && (elementInView === null || elementInView === void 0 ? void 0 : elementInView.target)) {
            setIsVisible((targetRef === null || targetRef === void 0 ? void 0 : targetRef.current) === elementInView.target);
        }
        else {
            setIsVisible(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetRef === null || targetRef === void 0 ? void 0 : targetRef.current, elementInView === null || elementInView === void 0 ? void 0 : elementInView.target]);
    return {
        setElements,
        elementInView,
        isVisible
    };
};

const useInterval = (callback, delay) => {
    const savedCallback = useRef(callback);
    const intervalIdRef = useRef(null);
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);
    useEffect(() => {
        const start = () => {
            savedCallback.current();
        };
        if (!(intervalIdRef === null || intervalIdRef === void 0 ? void 0 : intervalIdRef.current))
            return;
        if (delay !== null) {
            intervalIdRef.current = window.setInterval(start, delay);
        }
        const id = intervalIdRef.current;
        return () => {
            window.clearInterval(id);
        };
    }, [delay]);
    const resetInterval = () => {
        window.clearInterval(intervalIdRef.current || 0);
        intervalIdRef.current = window.setInterval(() => {
            savedCallback.current();
        }, delay);
    };
    const pauseInterval = () => {
        window.clearInterval(intervalIdRef.current || 0);
    };
    const resumeInterval = () => {
        intervalIdRef.current = window.setInterval(() => {
            savedCallback.current();
        }, delay);
    };
    return { resetInterval, pauseInterval, resumeInterval };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function assignRef(ref, value) {
    if (ref == null)
        return;
    if (typeof ref === "function") {
        ref(value);
        return;
    }
    try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref.current = value;
    }
    catch (error) {
        throw new Error(`Cannot assign value '${value}' to ref '${ref}'`);
    }
}
function useMergeRefs(...refs) {
    return useMemo(() => {
        if (refs.every((ref) => ref == null)) {
            return null;
        }
        return (node) => {
            refs.forEach((ref) => {
                if (ref)
                    assignRef(ref, node);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, refs);
}

function useWindowSize() {
    const [windowSize, setWindowSize] = useState({
        width: 0,
        height: 0,
    });
    const handleSize = () => {
        setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
        });
    };
    useEventListener('resize', handleSize);
    // Set size at the first client-side load
    useIsomorphicLayoutEffect(() => {
        handleSize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return windowSize;
}

const useOnClickOutside = (ref, handler) => {
    useEffect(() => {
        const listener = (event) => {
            const el = ref === null || ref === void 0 ? void 0 : ref.current;
            if (!el || el.contains((event === null || event === void 0 ? void 0 : event.target) || null))
                return;
            handler(event);
        };
        if (!(ref === null || ref === void 0 ? void 0 : ref.current))
            return;
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, handler]); // Reload only if ref or handler changes
};

const useOnKeyPress = (ref, handler, key, keyEvent = "keydown") => {
    useEffect(() => {
        const listener = (event) => {
            const targetKeys = [...key];
            if (!targetKeys.some(targetKey => targetKey === event.key))
                return;
            handler(event);
        };
        if (!(ref === null || ref === void 0 ? void 0 : ref.current))
            return;
        document.addEventListener(keyEvent, listener);
        return () => {
            document.removeEventListener(keyEvent, listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, handler]);
};

function useMediaQuery({ min, max }) {
    const query = max ? `(max-width: ${max}px)` : `(min-width: ${min}px)`;
    const getMatches = (query) => {
        // Prevents SSR issues
        if (typeof window !== 'undefined') {
            return window.matchMedia(query).matches;
        }
        return false;
    };
    const [matches, setMatches] = useState(getMatches(query));
    function handleChange() {
        setMatches(getMatches(query));
    }
    useEffect(() => {
        const matchMedia = window.matchMedia(query);
        // Triggered at the first client-side load and if query changes
        handleChange();
        // Listen matchMedia
        if (matchMedia.addListener) {
            matchMedia.addListener(handleChange);
        }
        else {
            matchMedia.addEventListener('change', handleChange);
        }
        return () => {
            if (matchMedia.removeListener) {
                matchMedia.removeListener(handleChange);
            }
            else {
                matchMedia.removeEventListener('change', handleChange);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);
    return matches;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function useCopyToClipboard() {
    const [copiedText, setCopiedText] = useState(null);
    const copy = (text) => __awaiter(this, void 0, void 0, function* () {
        if (!(navigator === null || navigator === void 0 ? void 0 : navigator.clipboard)) {
            console.warn('Clipboard not supported');
            return false;
        }
        // Try to save to clipboard then save it in the state if worked
        try {
            yield navigator.clipboard.writeText(text);
            setCopiedText(text);
            return true;
        }
        catch (error) {
            console.warn('Copy failed', error);
            setCopiedText(null);
            return false;
        }
    });
    return [copiedText, copy];
}

function useElementSize() {
    // Mutable values like 'ref.current' aren't valid dependencies
    // because mutating them doesn't re-render the component.
    // Instead, we use a state as a ref to be reactive.
    const [ref, setRef] = useState(null);
    const [size, setSize] = useState({
        width: 0,
        height: 0,
    });
    // Prevent too many rendering using useCallback
    const handleSize = useCallback(() => {
        setSize({
            width: (ref === null || ref === void 0 ? void 0 : ref.offsetWidth) || 0,
            height: (ref === null || ref === void 0 ? void 0 : ref.offsetHeight) || 0,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref === null || ref === void 0 ? void 0 : ref.offsetHeight, ref === null || ref === void 0 ? void 0 : ref.offsetWidth]);
    useEventListener('resize', handleSize);
    useIsomorphicLayoutEffect(() => {
        handleSize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref === null || ref === void 0 ? void 0 : ref.offsetHeight, ref === null || ref === void 0 ? void 0 : ref.offsetWidth]);
    return [setRef, size];
}

function useIntersectionObserver(elementRef, { threshold = 0, root = null, rootMargin = '0%', freezeOnceVisible = false, }) {
    const [entry, setEntry] = useState();
    const frozen = (entry === null || entry === void 0 ? void 0 : entry.isIntersecting) && freezeOnceVisible;
    const updateEntry = ([entry]) => {
        setEntry(entry);
    };
    useEffect(() => {
        const node = elementRef || null; // DOM Ref
        const hasIOSupport = !!window.IntersectionObserver;
        if (!hasIOSupport || frozen || !node)
            return;
        const observerParams = { threshold, root, rootMargin };
        const observer = new IntersectionObserver(updateEntry, observerParams);
        observer.observe(node);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elementRef, JSON.stringify(threshold), root, rootMargin, frozen]);
    return entry;
}

export { REACT_ELEMENT_TYPE, REACT_FRAGMENT_TYPE, assignRef, flattenChildren, getTypeOfDevice, isFragment, isValidObject, useCopyToClipboard, useElementSize, useEventListener, useIntersectionObserver, useInterval, useIsomorphicLayoutEffect, useMediaQuery, useMergeRefs, useObserveElements, useOnClickOutside, useOnKeyPress, useScrollPosition, useWindowSize };
